import React, { useEffect, useState } from 'react'
import { useChatContext } from 'stream-chat-react'
import type { DefaultStreamChatGenerics } from '../../types'
import appColors from '../../../../appColors'
import chatNoStarSvg from '../../../../assets/svgs/chat-no-star.svg'
import { StarredMessageItem } from './StarredMessageItem'

interface StarredMessagesProps {
  onClose?: () => void
}

interface PinnedMessage {
  id: string
  text: string
  user: any
  created_at: string
  pinned_at: string
  pinned_by: any
  attachments?: any[]
  latest_reactions?: any[]
  own_reactions?: any[]
  reaction_counts?: Record<string, number>
  reaction_scores?: Record<string, number>
  reply_count?: number
  deleted_reply_count?: number
  mentioned_users?: any[]
  silent?: boolean
  pinned: boolean
  cid: string
  type: string
  html?: string
  shadowed?: boolean
  deleted_at?: string
}

export const StarredMessages: React.FC<StarredMessagesProps> = React.memo(({ onClose }) => {
  const { channel, client } = useChatContext<DefaultStreamChatGenerics>()
  const [pinnedMessages, setPinnedMessages] = useState<PinnedMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPinnedMessages = async () => {
      if (!channel) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        // Fetch pinned messages from the current channel using the getPinnedMessages method
        const response = await channel.getPinnedMessages({
          limit: 50,
        })

        if (response.messages) {
          setPinnedMessages(response.messages as PinnedMessage[])
        } else {
          setPinnedMessages([])
        }
      } catch (err) {
        console.error('Error fetching pinned messages:', err)
        setError('Failed to load starred messages')
        setPinnedMessages([])
      } finally {
        setLoading(false)
      }
    }

    fetchPinnedMessages()

    // Listen for message updates to refresh pinned messages
    const handleChannelEvent = (event: any) => {
      // Refresh on any message update that might affect pinning
      if (
        event.type === 'message.updated' || 
        (event.message && 'pinned' in event.message)
      ) {
        fetchPinnedMessages()
      }
    }

    // Listen for window events to refresh when messages are starred/unstarred
    const handleWindowEvent = (event: any) => {
      if (event.detail && event.detail.type === 'message-starred') {
        fetchPinnedMessages()
      }
    }

    if (channel) {
      channel.on(handleChannelEvent)
    }

    window.addEventListener('chat-event', handleWindowEvent)

    return () => {
      if (channel) {
        channel.off(handleChannelEvent)
      }
      window.removeEventListener('chat-event', handleWindowEvent)
    }
  }, [channel])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-app-primary-60"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 mb-2">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="text-app-primary-60 hover:text-app-primary-80 underline"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (pinnedMessages.length === 0) {
    return (
      <div className="flex my-auto items-center justify-center">
        <div className="text-center my-auto">
          <div className="mb-6">
            <img src={chatNoStarSvg} alt="No starred messages" className="w-32 h-32 mx-auto" />
          </div>
          <h4 className="text-lg font-medium mb-2" style={{ color: appColors['app-black']['100'] }}>
            No starred messages
          </h4>
          <p className="text-sm text-app-grey-60">
            Star messages to save them for later by clicking the three dots menu on any message.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-350px)] overflow-y-auto">
      <div className="p-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-app-black-100">
            Starred Messages ({pinnedMessages.length})
          </h3>
          <p className="text-sm text-app-grey-60">
            Messages you've starred in this conversation
          </p>
        </div>
        
        <div className="space-y-2">
          {pinnedMessages.map((message) => (
            <StarredMessageItem 
              key={message.id} 
              message={message} 
              client={client}
              onUnstar={async (messageId) => {
                setPinnedMessages(prev => prev.filter(msg => msg.id !== messageId))
                // Dispatch custom event to notify other components
                window.dispatchEvent(new CustomEvent('chat-event', {
                  detail: { type: 'message-starred', messageId, pinned: false }
                }))
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
})
